/**
 * Maps currency codes to their corresponding symbols
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  const currencySymbols: Record<string, string> = {
    // Major currencies
    'USD': '$',      // US Dollar
    'EUR': '€',      // Euro
    'GBP': '£',      // British Pound
    'JPY': '¥',      // Japanese Yen
    'CNY': '¥',      // Chinese Yuan
    'CAD': 'CA$',    // Canadian Dollar
    'AUD': 'A$',     // Australian Dollar
    'CHF': 'CHF',    // Swiss Franc
    'SEK': 'kr',     // Swedish Krona
    'NOK': 'kr',     // Norwegian Krone
    'DKK': 'kr',     // Danish Krone
    
    // Other common currencies
    'INR': '₹',      // Indian Rupee
    'KRW': '₩',      // Korean Won
    'BRL': 'R$',     // Brazilian Real
    'MXN': '$',      // Mexican Peso
    'RUB': '₽',      // Russian Ruble
    'ZAR': 'R',      // South African Rand
    'SGD': 'S$',     // Singapore Dollar
    'HKD': 'HK$',    // Hong Kong Dollar
    'NZD': 'NZ$',    // New Zealand Dollar
    'TRY': '₺',      // Turkish Lira
    'PLN': 'zł',     // Polish Zloty
    'CZK': 'Kč',     // Czech Koruna
    'HUF': 'Ft',     // Hungarian Forint
    'ILS': '₪',      // Israeli Shekel
    'THB': '฿',      // Thai Baht
    'MYR': 'RM',     // Malaysian Ringgit
    'PHP': '₱',      // Philippine Peso
    'IDR': 'Rp',     // Indonesian Rupiah
    'VND': '₫',      // Vietnamese Dong
  };

  return currencySymbols[currencyCode.toUpperCase()] || currencyCode;
};

/**
 * Gets a list of common currency codes with their symbols for dropdowns
 */
export const getCommonCurrencies = () => [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'KRW', symbol: '₩', name: 'Korean Won' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
];

/**
 * Formats a number as currency using Intl.NumberFormat
 */
export const formatCurrency = (
  value: number, 
  currencyCode: string, 
  locale: string = 'en-US',
  minimumFractionDigits?: number,
  maximumFractionDigits?: number
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value);
  } catch (error) {
    // Fallback if currency code is not supported
    const symbol = getCurrencySymbol(currencyCode);
    return `${symbol}${value.toFixed(minimumFractionDigits || 2)}`;
  }
};
